import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, ReferenceArea, Customized
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { apiService, queryKeys } from '@/services/api';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import type { CSRTimelinePoint } from '@/types/api';
import {
  buildChartDateLabelMap,
  buildYearBackgroundBands,
  buildYearSections,
  makeYearLabelRenderer,
  renderHistoryPointDot,
  renderHistoryPointActiveDot,
  makeDateAxisTickRenderer,
} from '@/utils/historyChart';

export interface WeeklyChartPoint {
  date: string;
  monthDayDisplay: string;
  yearMarker?: string;
  fullDateDisplay: string;
  csr: number;
  result?: string | null;
  played: boolean;
  pointType: 'win' | 'loss' | 'missed';
  // Match detail fields
  opponent_name?: string | null;
  player_games_won?: number | null;
  opponent_games_won?: number | null;
  player_race?: number | null;
  opponent_race?: number | null;
  venue?: string | null;
  division_name?: string | null;
  division_id?: string | null;
  is_makeup_match?: boolean | null;
  csr_before?: number | null;
  // Multi-match same-day clustering
  same_day_index?: number | null;
  same_day_count?: number | null;
  xKey?: string;
  isSpacer?: boolean;
  isMatchDay?: boolean;
  isGroupedMatchDay?: boolean;
  // Multi-game-type fields (used in "All" mode)
  game_type?: string;
  csr_8ball?: number | null;
  csr_9ball?: number | null;
  csr_10ball?: number | null;
}

export function buildWeeklyChartData(timeline?: CSRTimelinePoint[]): WeeklyChartPoint[] {
  if (!timeline?.length) return [];

  const uniqueDates = [...new Set(timeline.map((pt) => pt.date.split('T')[0]))];
  const dateLabels = buildChartDateLabelMap(uniqueDates);

  // For same-day multi-match points, we keep the same date key so recharts
  // places them at the same x position. The dot renderer applies a small
  // pixel offset based on same_day_index to separate them visually.
  // Since recharts data array must have unique entries, we use the array
  // index to distinguish them — the Line connects them in order.
  return timeline.map((pt) => {
    const dateKey = pt.date.split('T')[0];
    const dateLabel = dateLabels.get(dateKey)!;
    const sameDayIndex = pt.same_day_index ?? 0;
    const sameDayCount = pt.same_day_count ?? 1;
    return {
      date: dateKey,
      monthDayDisplay: dateLabel.monthDayDisplay,
      yearMarker: dateLabel.yearMarker,
      fullDateDisplay: dateLabel.fullDateDisplay,
      csr: Math.round(pt.csr),
      result: pt.result,
      played: Boolean(pt.played),
      pointType: pt.point_type ?? 'missed',
      opponent_name: pt.opponent_name,
      player_games_won: pt.player_games_won,
      opponent_games_won: pt.opponent_games_won,
      player_race: pt.player_race,
      opponent_race: pt.opponent_race,
      venue: pt.venue,
      division_name: pt.division_name,
      division_id: pt.division_id,
      is_makeup_match: pt.is_makeup_match,
      csr_before: pt.csr_before,
      same_day_index: sameDayIndex,
      same_day_count: sameDayCount,
    };
  });
}

export function padMatchDayChartData(
  points: WeeklyChartPoint[],
  groupMatchDays = false,
  padAllMatchDays = false,
): WeeklyChartPoint[] {
  const pointsByDate = new Map<string, WeeklyChartPoint[]>();
  points.forEach((point) => {
    pointsByDate.set(point.date, [...(pointsByDate.get(point.date) ?? []), point]);
  });

  return [...pointsByDate.entries()].flatMap(([date, dayPoints]) => {
    const isMatchDay = dayPoints.some((point) => point.played);
    const shouldGroupMatchDay = groupMatchDays && isMatchDay && (
      padAllMatchDays || dayPoints.filter((point) => point.played).length > 1
    );
    const matchPoints = dayPoints.map((point, index) => ({
      ...point,
      xKey: `${date}#${index}`,
      isMatchDay,
      isGroupedMatchDay: shouldGroupMatchDay,
    }));
    if (!shouldGroupMatchDay) return matchPoints;

    // Duplicate the neighboring values only to create empty categorical space;
    // dot renderers suppress these spacers so played matches sit inside the band.
    return [
      { ...matchPoints[0], xKey: `${date}#before`, isSpacer: true },
      ...matchPoints,
      { ...matchPoints[matchPoints.length - 1], xKey: `${date}#after`, isSpacer: true },
    ];
  });
}

// ---------------------------------------------------------------------------
// Pool ball dots & multi-game-type support (mirrors HeadToHeadChart)
// ---------------------------------------------------------------------------
const BALL_COLORS: Record<string, { fill: string; text: string; label: string }> = {
  '8-ball':  { fill: '#1e1e1e', text: '#ffffff', label: '8' },
  '9-ball':  { fill: '#fbbf24', text: '#1e1e1e', label: '9' },
  '10-ball': { fill: '#3b82f6', text: '#ffffff', label: '10' },
};

const ALL_GAME_LINE_STYLES: { gameType: string; keyPrefix: string; dash: string; stroke: string }[] = [
  { gameType: '8-ball',  keyPrefix: '8ball',  dash: '',     stroke: '#374151' },
  { gameType: '9-ball',  keyPrefix: '9ball',  dash: '6 3',  stroke: '#d97706' },
  { gameType: '10-ball', keyPrefix: '10ball', dash: '2 3',  stroke: '#2563eb' },
];

function PoolBallDot({ cx, cy, gameType, isWin, offsetX = 0, onClick, onMouseEnter, onTouchEnd }: {
  cx: number; cy: number; gameType: string; isWin: boolean; offsetX?: number;
  onClick?: () => void; onMouseEnter?: () => void; onTouchEnd?: (e: React.TouchEvent) => void;
}) {
  const ball = BALL_COLORS[gameType] ?? BALL_COLORS['8-ball'];
  const r = 8;
  const haloColor = isWin ? '#10b981' : '#ef4444';
  const x = cx + offsetX;
  return (
    <g style={{ cursor: onClick ? 'pointer' : 'default' }}
       onClick={onClick} onMouseEnter={onMouseEnter} onTouchEnd={onTouchEnd}>
      <circle cx={x} cy={cy} r={r + 4} fill={haloColor} opacity={0.25} />
      <circle cx={x} cy={cy} r={r + 2} fill={haloColor} opacity={0.4} />
      <circle cx={x} cy={cy} r={r} fill={ball.fill} stroke="#fff" strokeWidth={1.5} />
      <text x={x} y={cy} textAnchor="middle" dominantBaseline="central"
        fill={ball.text} fontSize={ball.label.length > 1 ? 7.5 : 8.5} fontWeight="bold"
        style={{ pointerEvents: 'none' }}>
        {ball.label}
      </text>
    </g>
  );
}

function buildMultiGameChartData(
  timeline8?: CSRTimelinePoint[],
  timeline9?: CSRTimelinePoint[],
  timeline10?: CSRTimelinePoint[],
): WeeklyChartPoint[] {
  const tagged: (CSRTimelinePoint & { game_type: string })[] = [
    ...(timeline8 ?? []).map(pt => ({ ...pt, game_type: '8-ball' })),
    ...(timeline9 ?? []).map(pt => ({ ...pt, game_type: '9-ball' })),
    ...(timeline10 ?? []).map(pt => ({ ...pt, game_type: '10-ball' })),
  ].filter(pt => pt.played);

  tagged.sort((a, b) => {
    const da = a.date.split('T')[0];
    const db = b.date.split('T')[0];
    if (da !== db) return da.localeCompare(db);
    return a.game_type.localeCompare(b.game_type);
  });

  if (!tagged.length) return [];

  const dateCounts = new Map<string, number>();
  for (const pt of tagged) {
    const dk = pt.date.split('T')[0];
    dateCounts.set(dk, (dateCounts.get(dk) ?? 0) + 1);
  }
  const dateIndices = new Map<string, number>();
  const uniqueDates = [...new Set(tagged.map(pt => pt.date.split('T')[0]))];
  const dateLabels = buildChartDateLabelMap(uniqueDates);

  return tagged.map(pt => {
    const dateKey = pt.date.split('T')[0];
    const dateLabel = dateLabels.get(dateKey)!;
    const sameDayCount = dateCounts.get(dateKey) ?? 1;
    const currentIndex = dateIndices.get(dateKey) ?? 0;
    dateIndices.set(dateKey, currentIndex + 1);

    const csr = Math.round(pt.csr);
    return {
      date: dateKey,
      monthDayDisplay: dateLabel.monthDayDisplay,
      yearMarker: dateLabel.yearMarker,
      fullDateDisplay: dateLabel.fullDateDisplay,
      csr,
      result: pt.result,
      played: true,
      pointType: (pt.point_type ?? (pt.result === 'W' ? 'win' : 'loss')) as 'win' | 'loss' | 'missed',
      opponent_name: pt.opponent_name,
      player_games_won: pt.player_games_won,
      opponent_games_won: pt.opponent_games_won,
      player_race: pt.player_race,
      opponent_race: pt.opponent_race,
      venue: pt.venue,
      division_name: pt.division_name,
      division_id: pt.division_id,
      is_makeup_match: pt.is_makeup_match,
      csr_before: pt.csr_before,
      same_day_index: currentIndex,
      same_day_count: sameDayCount,
      game_type: pt.game_type,
      csr_8ball: pt.game_type === '8-ball' ? csr : null,
      csr_9ball: pt.game_type === '9-ball' ? csr : null,
      csr_10ball: pt.game_type === '10-ball' ? csr : null,
    };
  });
}

interface PlayerHistoryChartProps {
  playerId: string;
  playerName: string;
  gameType: '8-ball' | '9-ball' | '10-ball' | 'all';
  days?: number;
  divisionId?: string;
  groupMatchDays?: boolean;
  className?: string;
  /** Callback to launch H2H view for this player vs an opponent. */
  onHeadToHead?: (playerId: string, opponentName: string) => void;
}

export const PlayerHistoryChart: React.FC<PlayerHistoryChartProps> = ({
  playerId,
  playerName: _playerName,
  gameType,
  days,
  divisionId,
  groupMatchDays = true,
  className = '',
  onHeadToHead,
}) => {
  // Hovered point shown in the static detail panel below the chart.
  // Set by dot mouseEnter; cleared on chart mouseLeave.
  // Click "pins" it (sticky = true) so it stays until clicked elsewhere.
  const [hoveredPoint, setHoveredPoint] = useState<WeeklyChartPoint | null>(null);
  const [sticky, setSticky] = useState(false);
  const hoverRef = useRef<(point: WeeklyChartPoint | null) => void>();
  hoverRef.current = (point) => {
    if (sticky) return; // don't override a pinned selection on hover
    setHoveredPoint(point);
  };

  const isAllMode = gameType === 'all';

  const { data: csrData, isLoading, error } = useQuery({
    queryKey: queryKeys.csrTimeline(playerId, gameType, divisionId ? undefined : days, divisionId),
    queryFn: () => apiService.getCSRTimeline(playerId, gameType, divisionId ? undefined : days, divisionId),
    staleTime: 5 * 60 * 1000,
    enabled: !isAllMode,
  });

  // All-mode: fetch each game type independently
  const { data: all8Data, isLoading: loading8, error: err8 } = useQuery({
    queryKey: queryKeys.csrTimeline(playerId, '8-ball', divisionId ? undefined : days, divisionId),
    queryFn: () => apiService.getCSRTimeline(playerId, '8-ball', divisionId ? undefined : days, divisionId),
    staleTime: 5 * 60 * 1000,
    enabled: isAllMode,
  });
  const { data: all9Data, isLoading: loading9, error: err9 } = useQuery({
    queryKey: queryKeys.csrTimeline(playerId, '9-ball', divisionId ? undefined : days, divisionId),
    queryFn: () => apiService.getCSRTimeline(playerId, '9-ball', divisionId ? undefined : days, divisionId),
    staleTime: 5 * 60 * 1000,
    enabled: isAllMode,
  });
  const { data: all10Data, isLoading: loading10, error: err10 } = useQuery({
    queryKey: queryKeys.csrTimeline(playerId, '10-ball', divisionId ? undefined : days, divisionId),
    queryFn: () => apiService.getCSRTimeline(playerId, '10-ball', divisionId ? undefined : days, divisionId),
    staleTime: 5 * 60 * 1000,
    enabled: isAllMode,
  });

  const allLoading = isAllMode ? (loading8 || loading9 || loading10) : isLoading;
  const allError = isAllMode ? (err8 || err9 || err10) : error;

  const chartData = useMemo(() => {
    const unpaddedData = isAllMode
      ? buildMultiGameChartData(all8Data?.timeline, all9Data?.timeline, all10Data?.timeline)
      : buildWeeklyChartData(csrData?.timeline);

    return padMatchDayChartData(unpaddedData, groupMatchDays, Boolean(divisionId));
  }, [isAllMode, csrData, all8Data, all9Data, all10Data, divisionId, groupMatchDays]);

  const axisTick = useMemo(
    () => makeDateAxisTickRenderer(new Map(
      chartData
        .filter((point) => !point.isSpacer)
        .map((point) => [point.xKey!, point])
    )),
    [chartData]
  );

  const yearBands = useMemo(
    () => buildYearBackgroundBands(chartData.map((point) => point.date)).map((band) => ({
      ...band,
      startDate: chartData.find((point) => point.date === band.startDate)?.xKey ?? band.startDate,
      endDate: [...chartData].reverse().find((point) => point.date === band.endDate)?.xKey ?? band.endDate,
    })),
    [chartData]
  );

  const yearSections = useMemo(
    () => buildYearSections(chartData.map((point) => point.date)).map((section) => ({
      ...section,
      startDate: chartData.find((point) => point.date === section.startDate)?.xKey ?? section.startDate,
      endDate: [...chartData].reverse().find((point) => point.date === section.endDate)?.xKey ?? section.endDate,
    })),
    [chartData]
  );

  const matchDayBands = useMemo(() => {
    if (!groupMatchDays) return [];

    return [...new Set(
      chartData
        .filter((point) => !point.isSpacer && point.isGroupedMatchDay)
        .map((point) => point.date)
    )].map((date) => ({
      x1: `${date}#before`,
      x2: `${date}#after`,
      label: new Date(`${date}T12:00:00`).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      }),
    }));
  }, [chartData, divisionId, groupMatchDays]);

  const yearLabelRenderer = useMemo(
    () => makeYearLabelRenderer(yearSections),
    [yearSections]
  );

  // Dot hover → update static panel. Dot click → pin selection.
  const renderDot = useCallback((props: any) => {
    const payload = props?.payload as WeeklyChartPoint | undefined;
    if (payload?.isSpacer) return <g />;
    const onMouseEnter = () => { if (payload && payload.pointType !== 'missed') hoverRef.current?.(payload); };
    const onClick = payload && payload.pointType !== 'missed'
      ? () => { setSticky(true); setHoveredPoint(payload); }
      : undefined;
    return renderHistoryPointDot({ ...props, onMouseEnter, onClick });
  }, []);

  const renderActiveDot = useCallback((props: any) => {
    const payload = props?.payload as WeeklyChartPoint | undefined;
    if (payload?.isSpacer) return <g />;
    const onMouseEnter = () => { if (payload && payload.pointType !== 'missed') hoverRef.current?.(payload); };
    const onClick = payload && payload.pointType !== 'missed'
      ? () => { setSticky(true); setHoveredPoint(payload); }
      : undefined;
    return renderHistoryPointActiveDot({ ...props, onMouseEnter, onClick });
  }, []);

  // Pool ball dot renderer for "All" mode
  const makePoolBallDotRenderer = useCallback((forGameType: string) => {
    return (props: any) => {
      const { cx, cy, payload } = props;
      if (cx == null || cy == null || !payload || payload.isSpacer) return <g />;
      if (payload.game_type !== forGameType || !payload.played) return <g />;

      const isWin = payload.pointType === 'win';
      const sdc = payload.same_day_count ?? 1;
      const sdi = payload.same_day_index ?? 0;
      const offsetX = sdc > 1 ? (sdi - (sdc - 1) / 2) * 12 : 0;

      const handleClick = () => { setSticky(true); setHoveredPoint(payload); };
      const handleMouseEnter = () => hoverRef.current?.(payload);
      const handleTouchEnd = (e: React.TouchEvent) => {
        e.preventDefault(); e.stopPropagation();
        setSticky(true); setHoveredPoint(payload);
      };

      return <PoolBallDot cx={cx} cy={cy} gameType={forGameType} isWin={isWin} offsetX={offsetX}
        onClick={handleClick} onMouseEnter={handleMouseEnter} onTouchEnd={handleTouchEnd} />;
    };
  }, []);

  // Which game types have data (for "All" mode line rendering)
  const presentGameTypes = useMemo(() => {
    if (!isAllMode) return [];
    const types = new Set(chartData.filter((point) => !point.isSpacer).map(p => p.game_type).filter(Boolean));
    return ALL_GAME_LINE_STYLES.filter(s => types.has(s.gameType));
  }, [isAllMode, chartData]);

  // Summary
  const summary = useMemo(() => {
    if (isAllMode) return null;
    const actualPoints = chartData.filter((point) => !point.isSpacer);
    if (!actualPoints.length) return null;
    const played = actualPoints.filter(p => p.played);
    const latest = actualPoints[actualPoints.length - 1].csr;
    const earliest = actualPoints[0].csr;
    const wins = played.filter(p => p.pointType === 'win').length;
    const losses = played.filter(p => p.pointType === 'loss').length;
    return { latest, change: latest - earliest, wins, losses };
  }, [isAllMode, chartData]);

  const allModeSummary = useMemo(() => {
    if (!isAllMode) return null;
    const summaries: { gameType: string; latest: number; change: number; wins: number; losses: number }[] = [];
    for (const gt of ['8-ball', '9-ball', '10-ball']) {
      const points = chartData.filter(p => !p.isSpacer && p.game_type === gt);
      if (points.length) {
        const latest = points[points.length - 1].csr;
        const earliest = points[0].csr;
        const wins = points.filter(p => p.pointType === 'win').length;
        const losses = points.filter(p => p.pointType === 'loss').length;
        summaries.push({ gameType: gt, latest, change: latest - earliest, wins, losses });
      }
    }
    return summaries.length ? summaries : null;
  }, [isAllMode, chartData]);

  // Opponent stats: fetch ALL game types with no time/division filter so the
  // table reflects the player's full history, not just current chart filters.
  const { data: opp8 } = useQuery({
    queryKey: queryKeys.csrTimeline(playerId, '8-ball', undefined, undefined),
    queryFn: () => apiService.getCSRTimeline(playerId, '8-ball'),
    staleTime: 5 * 60 * 1000,
  });
  const { data: opp9 } = useQuery({
    queryKey: queryKeys.csrTimeline(playerId, '9-ball', undefined, undefined),
    queryFn: () => apiService.getCSRTimeline(playerId, '9-ball'),
    staleTime: 5 * 60 * 1000,
  });
  const { data: opp10 } = useQuery({
    queryKey: queryKeys.csrTimeline(playerId, '10-ball', undefined, undefined),
    queryFn: () => apiService.getCSRTimeline(playerId, '10-ball'),
    staleTime: 5 * 60 * 1000,
  });

  const opponentStats = useMemo(() => {
    const allPoints = [
      ...(opp8?.timeline ?? []),
      ...(opp9?.timeline ?? []),
      ...(opp10?.timeline ?? []),
    ];
    const map = new Map<string, { wins: number; losses: number }>();
    for (const pt of allPoints) {
      if (!pt.played || !pt.opponent_name) continue;
      const name = pt.opponent_name;
      const entry = map.get(name) ?? { wins: 0, losses: 0 };
      if (pt.result === 'W') entry.wins++;
      else if (pt.result === 'L') entry.losses++;
      map.set(name, entry);
    }
    return [...map.entries()]
      .map(([name, { wins, losses }]) => ({ name, matches: wins + losses, wins, losses }))
      .sort((a, b) => b.matches - a.matches || a.name.localeCompare(b.name));
  }, [opp8, opp9, opp10]);

  const [showOpponents, setShowOpponents] = useState(false);

  if (allLoading) {
    return <div className={`flex items-center justify-center h-80 ${className}`}><LoadingSpinner size="lg" /></div>;
  }

  if (allError) {
    return (
      <div className={`flex items-center justify-center h-80 text-red-500 ${className}`}>
        <p className="text-sm">Failed to load history: {allError instanceof Error ? allError.message : 'Unknown error'}</p>
      </div>
    );
  }

  if (!chartData.length) {
    return (
      <div className={`flex items-center justify-center h-80 ${className}`}>
        <div className="text-center">
          <p className="text-slate-500 font-medium">No history available</p>
          <p className="text-slate-400 text-sm mt-1">No recorded data for {gameType === 'all' ? 'any game type' : gameType}{days ? ` in the last ${days} days` : ''}.</p>
        </div>
      </div>
    );
  }

  // Format date for display
  const formatDate = (d: string) => {
    const dt = new Date(d + (d.includes('T') ? '' : 'T12:00:00'));
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const hp = hoveredPoint; // alias for JSX readability
  const isWin = hp?.result === 'W' || hp?.pointType === 'win';
  const hasScore = hp && hp.player_games_won != null && hp.opponent_games_won != null;
  const hasRace = hp && hp.player_race != null && hp.opponent_race != null;

  return (
    <div className={className}>
      {/* Chart */}
      <div
        className="relative h-72 sm:h-80"
        onMouseLeave={() => { if (!sticky) setHoveredPoint(null); }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ top: 10, right: 10, left: 0, bottom: 12 }}
            onClick={() => { if (sticky) { setSticky(false); setHoveredPoint(null); } }}
          >
            {yearBands.map((band) => (
              <ReferenceArea
                key={band.year}
                x1={band.startDate}
                x2={band.endDate}
                fill="#f8fafc"
                fillOpacity={0.9}
                strokeOpacity={0}
              />
            ))}
            {matchDayBands.map((band) => (
              <ReferenceArea
                key={band.x1}
                x1={band.x1}
                x2={band.x2}
                fill="#eef2ff"
                fillOpacity={0.8}
                stroke="#c7d2fe"
                strokeOpacity={0.8}
                label={{
                  value: band.label,
                  position: 'insideTop',
                  fill: '#6366f1',
                  fontSize: 10,
                }}
              />
            ))}
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis
              dataKey="xKey"
              tick={axisTick}
              tickLine={false}
              axisLine={false}
              height={yearSections.length > 1 ? 82 : 48}
              interval="preserveStartEnd"
            />
            <YAxis
              fontSize={11}
              stroke="#94a3b8"
              tickLine={false}
              axisLine={false}
              domain={['dataMin - 2', 'dataMax + 2']}
              width={40}
            />
            <Customized component={yearLabelRenderer} />
            {isAllMode ? (
              /* "All" mode: one line per game type with pool ball dots */
              presentGameTypes.map((style) => (
                <Line
                  key={style.gameType}
                  type="monotone"
                  dataKey={`csr_${style.keyPrefix}`}
                  stroke={style.stroke}
                  strokeWidth={2}
                  strokeDasharray={style.dash || undefined}
                  dot={makePoolBallDotRenderer(style.gameType)}
                  activeDot={makePoolBallDotRenderer(style.gameType)}
                  connectNulls
                  name={style.gameType}
                />
              ))
            ) : (
              <Line
                type="monotone"
                dataKey="csr"
                stroke="#6366f1"
                strokeWidth={3}
                dot={renderDot}
                activeDot={renderActiveDot}
                name="CSR"
                connectNulls
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
      {matchDayBands.length > 0 && (
        <p className="mt-2 text-xs text-slate-400">
          Shaded bands mark match days.
        </p>
      )}

      {/* Static detail panel — replaces floating tooltip */}
      <div className="mt-2 pt-3 border-t border-slate-100 min-h-[4.5rem]">
        {hp ? (
          <div className="flex flex-wrap items-start gap-x-6 gap-y-1 text-sm">
            {/* Game type badge (all mode) */}
            {hp.game_type && (() => {
              const ball = BALL_COLORS[hp.game_type];
              return ball ? (
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[9px] font-bold"
                  style={{ backgroundColor: ball.fill, color: ball.text }}>
                  {ball.label}
                </span>
              ) : null;
            })()}
            {/* Date + Result */}
            <div className="flex items-center gap-2">
              <span className="text-slate-500 font-medium">{formatDate(hp.date)}</span>
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold ${
                isWin ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'
              }`}>
                {isWin ? 'WIN' : 'LOSS'}
              </span>
            </div>
            {/* Opponent */}
            {hp.opponent_name && (
              <div className="flex items-center gap-1.5">
                <span className="text-slate-400">vs.</span>
                <span className="text-slate-700 font-medium">{hp.opponent_name}</span>
                {onHeadToHead && sticky && (
                  <button
                    onClick={() => onHeadToHead(playerId, hp.opponent_name!)}
                    className="ml-1 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-600 bg-indigo-50 rounded hover:bg-indigo-100 transition-colors"
                    title="View head-to-head history"
                  >
                    H2H
                  </button>
                )}
              </div>
            )}
            {/* Score */}
            {hasScore && (
              <span className="text-slate-600">
                <span className="text-slate-400 mr-1">Score</span>
                <span className="font-semibold">{hp.player_games_won}–{hp.opponent_games_won}</span>
              </span>
            )}
            {/* Race */}
            {hasRace && (
              <span className="text-slate-600">
                <span className="text-slate-400 mr-1">Race</span>
                <span className="font-semibold">{hp.player_race} to {hp.opponent_race}</span>
              </span>
            )}
            {/* CSR */}
            <span className="text-slate-600 flex items-center gap-1">
              <span className="text-slate-400">CSR</span>
              {hp.csr_before != null && (
                <>
                  <span className="text-slate-400">{hp.csr_before}</span>
                  <span className="text-slate-300">→</span>
                </>
              )}
              <span className="font-semibold">{hp.csr}</span>
              {hp.csr_before != null && (
                <span className={`text-[10px] font-medium ${
                  hp.csr > hp.csr_before ? 'text-emerald-600' : hp.csr < hp.csr_before ? 'text-red-500' : 'text-slate-400'
                }`}>
                  {hp.csr > hp.csr_before ? '+' : ''}{hp.csr - hp.csr_before}
                </span>
              )}
            </span>
            {/* Division */}
            {hp.division_name && (
              <span className="text-slate-500 text-xs">{hp.division_name}</span>
            )}
            {/* Makeup */}
            {hp.is_makeup_match && (
              <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 border border-amber-200">
                Makeup
              </span>
            )}
          </div>
        ) : (
          /* Default: show CSR summary when nothing is hovered */
          allModeSummary ? (
            <div className="flex flex-wrap items-center gap-4 text-sm">
              {allModeSummary.map(({ gameType: gt, latest, change, wins, losses }) => {
                const ball = BALL_COLORS[gt];
                return (
                  <div key={gt} className="flex items-center gap-1.5">
                    <span className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[7px] font-bold"
                      style={{ backgroundColor: ball.fill, color: ball.text }}>
                      {ball.label}
                    </span>
                    <span className="text-slate-500">CSR:</span>
                    <span className="font-semibold text-slate-800">{latest}</span>
                    {change !== 0 && (
                      <span className={`text-xs font-medium ${change > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                        {change > 0 ? '+' : ''}{change}
                      </span>
                    )}
                    <span className="text-xs tabular-nums">
                      <span className="text-emerald-600 font-medium">{wins}W</span>
                      <span className="text-slate-300 mx-0.5">–</span>
                      <span className="text-red-500 font-medium">{losses}L</span>
                    </span>
                  </div>
                );
              })}
              <span className="text-xs text-slate-400">Hover a match point for details</span>
            </div>
          ) : summary ? (
            <div className="flex items-center gap-2 text-sm">
              <div className="w-2.5 h-2.5 rounded-full bg-indigo-500" />
              <span className="text-slate-500">CSR:</span>
              <span className="font-semibold text-slate-800">{summary.latest}</span>
              {summary.change !== 0 && (
                <span className={`text-xs font-medium ${summary.change > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                  {summary.change > 0 ? '+' : ''}{summary.change}
                </span>
              )}
              <span className="text-xs tabular-nums">
                <span className="text-emerald-600 font-medium">{summary.wins}W</span>
                <span className="text-slate-300 mx-0.5">–</span>
                <span className="text-red-500 font-medium">{summary.losses}L</span>
              </span>
              <span className="text-xs text-slate-400 ml-2">Hover a match point for details</span>
            </div>
          ) : null
        )}
      </div>

      {/* Opponents summary — collapsible */}
      {opponentStats.length > 0 && (
        <div className="mt-1">
          <button
            onClick={() => setShowOpponents((v) => !v)}
            className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 transition-colors py-1"
          >
            <svg className={`w-3 h-3 transition-transform ${showOpponents ? 'rotate-90' : ''}`} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">Opponents</span>
            <span className="text-slate-400">({opponentStats.length})</span>
          </button>
          {showOpponents && (
            <div className="max-h-44 overflow-y-auto mt-0.5">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-slate-400 border-b border-slate-100">
                    <th className="text-left font-medium py-1 pl-1">Name</th>
                    <th className="text-center font-medium py-1 w-8">#</th>
                    <th className="text-center font-medium py-1 w-12">W-L</th>
                    <th className="py-1 w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {opponentStats.map((opp) => {
                    const winPct = opp.matches > 0 ? (opp.wins / opp.matches) * 100 : 0;
                    const clickable = !!onHeadToHead;
                    return (
                      <tr
                        key={opp.name}
                        className={`border-b border-slate-50 ${clickable ? 'cursor-pointer hover:bg-indigo-50/50 transition-colors' : ''}`}
                        onClick={clickable ? () => onHeadToHead(playerId, opp.name) : undefined}
                      >
                        <td className="py-1 pl-1 text-slate-700 font-medium truncate max-w-[10rem]">
                          {opp.name}
                        </td>
                        <td className="py-1 text-center text-slate-500">{opp.matches}</td>
                        <td className="py-1 text-center text-slate-600 font-medium tabular-nums">{opp.wins}-{opp.losses}</td>
                        <td className="py-1 pr-1">
                          <div className="flex h-2 rounded-full overflow-hidden bg-red-100">
                            <div className="bg-emerald-400 rounded-l-full" style={{ width: `${winPct}%` }} />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PlayerHistoryChart;
